import { setTasksByTaskStructureSharedUidLoader } from "../../../../../features/dataAccuracySlice";
import { DataAccuracyEndPointLogic } from "../../../../../logic/AssetManagement/Health/Data Accuracy/dataAccuracyEndPointLogic";
import { formatDateToDDMMYY, getSiteInfo } from "../../../../../utils/helpers";

type Task = {
  website: string;
  in_progress: number;
  last_updated: string;
  category: string;
  owner: string;
  assignee?: string;
  title: string | null;
  task_structure_shared_uid: string | null;
  to_do: number;
  in_review: number;
  snooze: number;
  priority: string;
  done: number;
};

type MergedTask = Omit<Task, "website" | "owner"> & {
  websites: string[];
  owners: string[];
  usernames: string[];
  assignees: number[];
};

function deduplicateTasks(tasks: Task[], usersArray: any[]): MergedTask[] {
  const taskMap = new Map<string, MergedTask>();

  // Build user lookup map for O(1) access
  const userMap = new Map<number, string>();
  for (const u of usersArray) {
    if (u.id != null) {
      userMap.set(u.id, `${u.first_name} ${u.last_name}`);
    }
  }

  for (const task of tasks) {
    if (!task.title || !task.task_structure_shared_uid) continue;

    const key = `${task.title}|${task.task_structure_shared_uid}`;
    const ownerId = Number(task.owner);
    const username = userMap.get(ownerId) || null;

    // Handle assignee - convert to number
    const assigneeId = Number(task.assignee);

    if (!taskMap.has(key)) {
      taskMap.set(key, {
        ...task,
        websites: [task.website],
        owners: [task.owner],
        usernames: username ? [username] : [],
        assignees: assigneeId ? [assigneeId] : [], // Initialize assignees array
      });
    } else {
      const existing = taskMap.get(key)!;

      // Sum up all the status counts
      existing.to_do += task.to_do;
      existing.in_progress += task.in_progress;
      existing.in_review += task.in_review;
      existing.snooze += task.snooze;
      existing.done += task.done;

      // Take the latest last_updated date
      if (task.last_updated) {
        const existingDate = new Date(existing.last_updated);
        const currentDate = new Date(task.last_updated);

        if (currentDate > existingDate) {
          existing.last_updated = task.last_updated;
        }
      }

      // Add unique websites
      if (!existing.websites.includes(task.website)) {
        existing.websites.push(task.website);
      }

      // Add unique owners
      if (!existing.owners.includes(task.owner)) {
        existing.owners.push(task.owner);
      }

      // Add unique usernames
      if (username && !existing.usernames.includes(username)) {
        existing.usernames.push(username);
      }

      // Add unique assignees
      if (assigneeId && !existing.assignees.includes(assigneeId)) {
        existing.assignees.push(assigneeId);
      }
    }
  }

  return Array.from(taskMap.values());
}

function filterAndGroupTasks(tasks: any, allowedUids: any) {
  if (!Array.isArray(tasks)) return [];
  const taskMap = new Map();

  for (const task of tasks) {
    // Skip if no title/uid
    if (!task.title || !task.task_structure_shared_uid) continue;

    // Skip if uid not in allowed list
    if (!allowedUids.includes(task.task_structure_shared_uid)) continue;

    const key = `${task.title}|${task.task_structure_shared_uid}`;

    if (!taskMap.has(key)) {
      taskMap.set(key, {
        ...task,
        websites: [task.website],
        owners: [task.owner],
      });
    } else {
      const existing = taskMap.get(key);

      // Aggregate numeric fields dynamically
      for (const [k, v] of Object.entries(task)) {
        if (typeof v === "number") {
          existing[k] = (existing[k] || 0) + v;
        }
      }

      // Collect unique websites and owners
      if (task.website && !existing.websites.includes(task.website)) {
        existing.websites.push(task.website);
      }
      if (task.owner && !existing.owners.includes(task.owner)) {
        existing.owners.push(task.owner);
      }
    }
  }

  return Array.from(taskMap.values());
}

function mapOwnersToNames(ownerIds: any, allUsers: any) {
  if (!Array.isArray(allUsers)) return [];

  // Convert single ID to array for consistent handling
  const ids = Array.isArray(ownerIds) ? ownerIds : [ownerIds];

  return ids
    .filter((id) => id !== null && id !== undefined && id !== "")
    .map((id) => {
      const user = allUsers.find((u) => String(u.id) === String(id));
      return user ? `${user.first_name} ${user.last_name}` : `Unknown (${id})`;
    });
}
// Extract missing query_output:* fields (present in data but NOT in log_data)
const getMissingQueryOutputFields = (row: any) => {
  if (!row?.data) return [];

  const allRootKeys = Object.keys(row.data).filter((k) =>
    k.startsWith("query_output:"),
  );

  const allLogKeys = new Set(
    row.data.log_data?.flatMap((entry: any) =>
      Object.keys(entry).filter((k) => k.startsWith("query_output:")),
    ) || [],
  );

  return allRootKeys.filter((k) => !allLogKeys.has(k));
};

type FetchDataAccuracyParams = {
  token: any;
  queryId: any;
  type: any;
  fetchBy: "Dimension" | "Organization" | string;
  selectedSites: any;
  filteredDimensionList: any[];
  dimensionList: any[];
  siteList: any[];
  taskStructureSharedUid?: any;
  assetSharedUid?: any;
  dispatch: any;
};

const fetchDataAccuracy = async ({
  token,
  queryId,
  type,
  fetchBy,
  selectedSites,
  filteredDimensionList,
  dimensionList,
  siteList,
  taskStructureSharedUid = "",
  assetSharedUid = "",
  dispatch,
}: FetchDataAccuracyParams) => {
  dispatch(setTasksByTaskStructureSharedUidLoader(true));

  try {
    await DataAccuracyEndPointLogic.getTasksByTaskStructureSharedUid(
      token,
      taskStructureSharedUid,
      assetSharedUid,
      dispatch,
    );

    await DataAccuracyEndPointLogic.getDataAccuracyByWebsiteOrDimensionOrOrganization(
      queryId,
      token,
      type,
      type === "site" ? selectedSites : [Object.keys(siteList[0])[0]],
      fetchBy === "Dimension"
        ? selectedSites
        : fetchBy === "Organization"
          ? filteredDimensionList
          : [dimensionList[0]],
      dispatch,
    );

    await DataAccuracyEndPointLogic.getAllUserList(token);
  } catch (error) {
    dispatch({
      type: "error",
      payload: { message: error },
    });
    throw error; // important for caller-level handling
  } finally {
    dispatch(setTasksByTaskStructureSharedUidLoader(false));
  }
};

/**
 * Exports the Data Accuracy tasks summary table as a CSV file.
 *
 * This function builds a CSV with one row per reference_id (grouped tasks).
 * Columns include: Asset name, dynamic data fields (query_output:* from main data),
 * last occurrence date, dynamic occurrence fields (query_output:* from log_data),
 * and operational fields (Status, Created, Updated, Assignee).
 *
 * The downloaded file is named after the alert title (e.g. "Qftd_budget_control_Export.csv").
 *
 * @param tasksByTaskStructureSharedUid - Array of task objects sharing the same task_structure_shared_uid.
 * @param allWebsites                  - Array of website/permission objects used to resolve website names.
 */
const exportDataAccuracyTasksToCSV = (
  tasksByTaskStructureSharedUid: any,
  allWebsites: any[]
) => {
  // Guard: nothing to export if the tasks array is empty or invalid
  if (
    !tasksByTaskStructureSharedUid ||
    !Array.isArray(tasksByTaskStructureSharedUid) ||
    tasksByTaskStructureSharedUid.length === 0
  ) {
    return;
  }

  // --- Step 1: Discover dynamic columns from all tasks ---
  // dataFields    → columns from main data object (excluding log_data), e.g. static query outputs
  // occurrenceFields → columns from log_data entries, e.g. per-run metric values
  const dataFields = new Map<string, any>();
  const occurrenceFields = new Map<string, any>();

  tasksByTaskStructureSharedUid.forEach((task: any) => {
    if (task.data) {
      // Extract data fields (query_output:* with order > 0) from the root data object
      const dataWithoutLogData = { ...task.data };
      delete dataWithoutLogData.log_data;

      Object.entries(dataWithoutLogData).forEach(
        ([key, fieldData]: [string, any]) => {
          if (
            fieldData &&
            typeof fieldData === "object" &&
            fieldData.hasOwnProperty("order") &&
            fieldData.order !== null &&
            fieldData.order !== undefined &&
            Number(fieldData.order) > 0 &&
            key.startsWith("query_output:")
          ) {
            if (!dataFields.has(key)) {
              dataFields.set(key, {
                field: key,
                headerName: fieldData.title,
                order: Number(fieldData.order),
              });
            }
          }
        }
      );

      // Extract occurrence fields (query_output:* with order > 0) from log_data entries
      if (task.data.log_data && Array.isArray(task.data.log_data)) {
        task.data.log_data.forEach((logEntry: any) => {
          Object.entries(logEntry).forEach(
            ([logKey, logData]: [string, any]) => {
              if (
                logData &&
                typeof logData === "object" &&
                logData.hasOwnProperty("order") &&
                logData.order !== null &&
                logData.order !== undefined &&
                Number(logData.order) > 0 &&
                logKey.startsWith("query_output:")
              ) {
                if (!occurrenceFields.has(logKey)) {
                  occurrenceFields.set(logKey, {
                    field: logKey,
                    headerName: logKey
                      .replace(/^query_output:/, "")
                      .replace(/^\w/, (c) => c.toUpperCase()),
                    order: Number(logData.order),
                  });
                }
              }
            }
          );
        });
      }
    }
  });

  // --- Step 2: Sort columns by their defined order ---
  const sortedDataFields = Array.from(dataFields.values()).sort(
    (a, b) => a.order - b.order
  );
  const sortedOccurrenceFields = Array.from(occurrenceFields.values()).sort(
    (a, b) => a.order - b.order
  );

  // --- Step 3: Group tasks by reference_id (each group becomes one CSV row) ---
  const groupedTasks = tasksByTaskStructureSharedUid.reduce(
    (acc: any, task: any) => {
      const referenceId = task.reference_id || "";
      if (!acc[referenceId]) acc[referenceId] = [];
      acc[referenceId].push(task);
      return acc;
    },
    {}
  );

  // --- Step 4: Build CSV header row ---
  const csvRows: any[] = [];
  const headers = [
    "Asset",
    ...sortedDataFields.map(
      (f) => `"${f.headerName?.replace(/"/g, '""') || ""}"`
    ),
    "Last Occurrence Date",
    ...sortedOccurrenceFields.map(
      (f) => `"${f.headerName?.replace(/"/g, '""') || ""}"`
    ),
    "Status",
    "Created",
    "Updated",
    "Assignee",
  ];
  csvRows.push(headers.join(","));

  // --- Step 5: Build one CSV row per grouped reference_id ---
  Object.values(groupedTasks).forEach((tasks: any) => {
    const firstTask = tasks[0];

    // Determine the last occurrence date from the most recent log_data entry
    let lastOccurrenceDate = " ";
    if (firstTask.data?.log_data && Array.isArray(firstTask.data.log_data)) {
      const lastLogEntry =
        firstTask.data.log_data[firstTask.data.log_data.length - 1];
      if (lastLogEntry && lastLogEntry["meta_data:start_run"]) {
        const startRunValue = lastLogEntry["meta_data:start_run"].value;
        if (startRunValue) lastOccurrenceDate = startRunValue;
      }
    }

    const rowValues = [];

    // Column: Asset — resolve website name from shared_uid
    const siteNameInfo =
      getSiteInfo(allWebsites, firstTask.website_shared_uid) || " ";
    const siteName = Array.isArray(siteNameInfo) ? siteNameInfo.join(", ") : String(siteNameInfo);
    rowValues.push(`"${siteName.replace(/"/g, '""')}"`);

    // Columns: Dynamic data fields (from root data, excluding log_data)
    const dataWithoutLogData = firstTask.data ? { ...firstTask.data } : {};
    delete dataWithoutLogData.log_data;

    sortedDataFields.forEach((field) => {
      const fieldData = dataWithoutLogData[field.field];
      const val =
        fieldData && fieldData.hasOwnProperty("value") ? fieldData.value : "";
      const valStr = val === null || val === undefined ? "" : String(val);
      rowValues.push(`"${valStr.replace(/"/g, '""')}"`);
    });

    // Column: Last Occurrence Date — formatted from the last log entry's start_run
    const formattedOccurDate =
      lastOccurrenceDate && lastOccurrenceDate !== " "
        ? formatDateToDDMMYY(lastOccurrenceDate)
        : "";
    rowValues.push(`"${formattedOccurDate}"`);

    // Columns: Dynamic occurrence fields (from the last log_data entry)
    const lastLogEntry =
      firstTask.data?.log_data && Array.isArray(firstTask.data.log_data)
        ? firstTask.data.log_data[firstTask.data.log_data.length - 1]
        : {};

    sortedOccurrenceFields.forEach((field) => {
      const logData = lastLogEntry[field.field];
      const val =
        logData && logData.hasOwnProperty("value") ? logData.value : "";
      const valStr = val === null || val === undefined ? "" : String(val);
      rowValues.push(`"${valStr.replace(/"/g, '""')}"`);
    });

    // Columns: Operational fields — Status, Created date, Updated date, Assignee
    rowValues.push(`"${firstTask.current_status || ""}"`);
    const createdDate = firstTask.created_at
      ? formatDateToDDMMYY(firstTask.created_at)
      : "";
    rowValues.push(`"${createdDate}"`);
    const updatedDate = firstTask.modified_at
      ? formatDateToDDMMYY(firstTask.modified_at)
      : "";
    rowValues.push(`"${updatedDate}"`);
    rowValues.push(
      `"${(firstTask.assignee_fullname || "").replace(/"/g, '""')}"`
    );

    csvRows.push(rowValues.join(","));
  });

  // --- Step 6: Create and trigger CSV download ---
  const csvContent = csvRows.join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;

  // Use the alert title as the filename, sanitised for filesystem safety
  const alertTitle = tasksByTaskStructureSharedUid[0]?.title || "Data_Accuracy";
  link.setAttribute(
    "download",
    `${alertTitle.replace(/[^a-zA-Z0-9_-]/g, "_")}_Export.csv`
  );

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Exports the occurrences table (log_data) for a single task as a CSV file.
 *
 * - Dynamically discovers columns from `query_output:*` fields inside each
 *   log_data entry, sorted by their `order` property.
 * - Includes an "Execution Time" column derived from `meta_data:start_run`.
 * - The downloaded file is named after the task title (e.g. "Qftd_budget_control.csv").
 *
 * @param task      - A single task object that contains `data.log_data` (the occurrences array).
 * @param taskTitle - The human-readable task name used as the CSV filename.
 */
const exportOccurrencesToCSV = (task: any, taskTitle: string) => {
  // Guard: nothing to export if log_data is missing or empty
  if (!task?.data?.log_data || !Array.isArray(task.data.log_data) || task.data.log_data.length === 0) {
    return;
  }

  const logData = task.data.log_data;

  // Collect all query_output fields from log_data entries with order > 0
  const occurrenceFields = new Map<string, { field: string; headerName: string; order: number }>();

  logData.forEach((logEntry: any) => {
    Object.entries(logEntry).forEach(([key, fieldData]: [string, any]) => {
      if (
        fieldData &&
        typeof fieldData === "object" &&
        fieldData.hasOwnProperty("order") &&
        fieldData.order !== null &&
        fieldData.order !== undefined &&
        Number(fieldData.order) > 0 &&
        key.startsWith("query_output:")
      ) {
        if (!occurrenceFields.has(key)) {
          occurrenceFields.set(key, {
            field: key,
            headerName: key
              .replace(/^query_output:/, "")
              .replace(/^\w/, (c) => c.toUpperCase()),
            order: Number(fieldData.order),
          });
        }
      }
    });
  });

  const sortedFields = Array.from(occurrenceFields.values()).sort(
    (a, b) => a.order - b.order
  );

  // Build CSV
  const csvRows: string[] = [];

  // Headers: Execution Time + dynamic fields
  const headers = [
    "Execution Time",
    ...sortedFields.map(
      (f) => `"${f.headerName?.replace(/"/g, '""') || ""}"`
    ),
  ];
  csvRows.push(headers.join(","));

  // Rows
  logData.forEach((logEntry: any) => {
    const rowValues: string[] = [];

    // Execution time from meta_data:start_run
    const executionTime = logEntry["meta_data:start_run"]?.value
      ? formatDateToDDMMYY(logEntry["meta_data:start_run"].value)
      : "";
    rowValues.push(`"${executionTime}"`);

    // Dynamic fields
    sortedFields.forEach((field) => {
      const fieldData = logEntry[field.field];
      const val =
        fieldData && fieldData.hasOwnProperty("value") ? fieldData.value : "";
      const valStr = val === null || val === undefined ? "" : String(val);
      rowValues.push(`"${valStr.replace(/"/g, '""')}"`);
    });

    csvRows.push(rowValues.join(","));
  });

  const csvContent = csvRows.join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;

  const sanitizedTitle = taskTitle.replace(/[^a-zA-Z0-9_-]/g, "_");
  link.setAttribute("download", `${sanitizedTitle}.csv`);

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export {
  deduplicateTasks,
  filterAndGroupTasks,
  mapOwnersToNames,
  getMissingQueryOutputFields,
  fetchDataAccuracy,
  exportDataAccuracyTasksToCSV,
  exportOccurrencesToCSV,
};
